import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Amplify } from "aws-amplify";
import awsconfig from "../../../aws-exports";
import { ConciergeChat } from "../../../pages/Chatbox/components/ConciergeChat";
import { observer } from "mobx-react";
import { useStore } from "../../../hooks";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Loading } from "../../loading";
import { Icon } from "../../../elements";
import { Sidebar } from "@ann/web-components";
import {
  Content,
  FloatingChatWrapper,
  CloseConciergeButton,
} from "../App.styled";
import { Wrapper, SidebarMobileStyles } from "./App.styled";
import TopBar from "./TopBar";
import { SidebarProvider, SidebarContext } from "./SidebarContext";
import { menuItems } from "../consts";
import CustomAccountModal from '../CustomerAccountModal';
import SwitchWorkspaceModal from "../../../elements/v2/SwitchWorkspaceModal";

Amplify.configure(awsconfig);

const theme = createTheme({
  palette: {
    primary: { main: "#3D3D3D" },
    secondary: { main: "#FF5722" },
  },
});

const MobileAppContent = observer(() => {
  const [isConciergeCollapsed, setIsConciergeCollapsed] = useState(true);
  const [isCustomAccountModalOpen, setIsCustomAccountModalOpen] = useState(false);
  const [customAccountModalItems, setCustomAccountModalItems] = useState([]);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const { authStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarOpen, toggleSidebar } = useContext(SidebarContext);

  // Detectar si estamos en rutas de workspace
  const isWorkspaceRoute = useMemo(
    () => location?.pathname?.startsWith("/v2/workspace"),
    [location?.pathname]
  );

  useEffect(() => {
    if (authStore.isLoading) {
      authStore.getGlobalInformation();
    }
  }, [authStore]);

  const handleAccountSelector = useCallback(
    (id) => {
      authStore.pickAccount(id);
      setIsCustomAccountModalOpen(false);
    },
    [authStore]
  );

  const getAccountSelector = useCallback(() => {
    return authStore.sortedAccountListByFavs.map((item) => ({
      text: item.name,
      id: item.id,
      onClick: () => handleAccountSelector(item.id)
    }));
  }, [authStore.sortedAccountListByFavs, handleAccountSelector]);

  const handleCustomAccountModal = useCallback(() => {
    if (authStore.hasPermission("CUSTOM_ACCOUNT_MODAL_PERMISSION")) {
      setCustomAccountModalItems(getAccountSelector());
      setIsCustomAccountModalOpen(true);
    }
  }, [authStore, getAccountSelector]);

  const closeAccountModal = useCallback(async () => {
    setIsCustomAccountModalOpen(false);
  }, []);

  const memoizedMenuItems = useMemo(() => {
    const filteredItems = menuItems(navigate).filter(item => authStore.hasPermission(item.permissionCode))
      .map(item => ({
        ...item, 
        customItems: item.customItems.filter(subitem => authStore.hasPermission(subitem.permissionCode)),
      }));
    
    // Insertar "Switch Accounts" después de "Home" solo en mobile
    const homeIndex = filteredItems.findIndex(item => item.text === "Home");
    if (homeIndex !== -1 && authStore.hasPermission("CUSTOM_ACCOUNT_MODAL_PERMISSION")) {
      const switchAccountsItem = {
        icon: "group",
        text: "Switch Accounts",
        title: "Switch Accounts",
        link: null,
        customItems: [],
        superAdminOnly: false,
        permissionCode: "CUSTOM_ACCOUNT_MODAL_PERMISSION",
        isMobileOnly: true,
        onClick: handleCustomAccountModal,
      };
      filteredItems.splice(homeIndex + 1, 0, switchAccountsItem);
    }
    
    return filteredItems;
  }, [navigate, authStore.isGroupSuperAdmin, authStore.isGroupAdmin, authStore.userPermissions.length, handleCustomAccountModal]);

  // Detectar el item activo basado en la ruta actual
  useEffect(() => {
    const currentPath = location.pathname;
    
    for (const item of memoizedMenuItems) {
      // Verificar si el item tiene link directo y coincide
      if (item.link) {
        const normalizedLink = item.link.replace(/\/$/, '');
        const normalizedPath = currentPath.replace(/\/$/, '');
        if (normalizedPath === normalizedLink || normalizedPath.startsWith(normalizedLink + '/')) {
          setActiveMenuItem(item.text);
          return;
        }
      }
      
      // Verificar si algún subitem coincide
    if (item.customItems && item.customItems.length > 0) {
        for (const subItem of item.customItems) {
          if (subItem.link) {
            const normalizedSubLink = subItem.link.replace(/\/$/, '');
            const normalizedPath = currentPath.replace(/\/$/, '');
            if (normalizedPath === normalizedSubLink || normalizedPath.startsWith(normalizedSubLink + '/')) {
              setActiveMenuItem(item.text);
              return;
            }
          }
        }
      }
    }
  }, [location.pathname, memoizedMenuItems]);


  return authStore.isLoading ? (
    <Loading />
  ) : (
    <>
      <SidebarMobileStyles />
      <Wrapper id="mobile-app-wrapper">
        <TopBar />
        <Sidebar
          key={`sidebar-${isSidebarOpen}`}
          id="sidebar-mobile"
          className={isSidebarOpen ? "sidebar-expanded" : "sidebar-collapsed"}
          defaultExpanded={isSidebarOpen}
          position="fixed"
          menuStyle="accordion"
          onToggle={(expanded) => {
            toggleSidebar();
          }}
        >
        {memoizedMenuItems.map((item, index) => {
          if (item.customItems && item.customItems.length > 0) {
            return (
              <Sidebar.Item
                key={item.text || index}
                icon={item.icon}
                text={item.text}
                isActive={activeMenuItem === item.text}
              >
                <Sidebar.Menu>
                  {item.customItems.map((subItem, subIndex) => (
                      <Sidebar.Menu.Item
                        key={subItem.text || subIndex}
                        title={subItem.text}
                        icon={subItem.icon}
                        onClick={() => {
                        setActiveMenuItem(item.text);
                          if (subItem.onClick) {
                            subItem.onClick();
                          }
                          toggleSidebar(); // Cerrar el sidebar después de navegar
                        }}
                      />
                  ))}
                </Sidebar.Menu>
              </Sidebar.Item>
            );
          } else {
            return (
              <Sidebar.Item
                key={item.text || index}
                icon={item.icon}
                text={item.text}
                isActive={activeMenuItem === item.text}
                onClick={() => {
                  setActiveMenuItem(item.text);
                  // Si tiene onClick personalizado (como Switch Accounts), usarlo
                  if (item.onClick) {
                    item.onClick();
                    toggleSidebar(); // Cerrar el sidebar después de abrir el modal
                  } else if (item.link) {
                    navigate(item.link);
                    toggleSidebar(); // Cerrar el sidebar después de navegar
                  }
                }}
              />
            );
          }
        })}
        </Sidebar>

        <Content id="main-content">
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Outlet />
          </ThemeProvider>
        </Content>

        <FloatingChatWrapper isCollapsed={isConciergeCollapsed}>
          {!isConciergeCollapsed && (
            <CloseConciergeButton
              onClick={() => setIsConciergeCollapsed(!isConciergeCollapsed)}
            >
              <Icon name={"widgetChat"} width={"inherit"} height={"inherit"} />
            </CloseConciergeButton>
          )}
          <ConciergeChat
            isCollapsed={isConciergeCollapsed}
            onClose={() => setIsConciergeCollapsed(true)}
          />
        </FloatingChatWrapper>

        {/* CUSTOM ACCOUNT MODAL - Show only when NOT in workspace routes */}
        {isCustomAccountModalOpen && !isWorkspaceRoute && (
          <CustomAccountModal
            items={customAccountModalItems}
            onClose={closeAccountModal}
          />
        )}

        {/* SWITCH WORKSPACE MODAL - Only show in workspace routes */}
        {isCustomAccountModalOpen && isWorkspaceRoute && (
          <SwitchWorkspaceModal
            isOpen={isCustomAccountModalOpen}
            onClose={closeAccountModal}
            onWorkspaceSelect={(workspace) => {
              console.log("Workspace seleccionado:", workspace);
              closeAccountModal();
            }}
          />
        )}
      </Wrapper>
    </>
  );
});

const MobileApp = observer(() => {
  return (
    <SidebarProvider>
      <MobileAppContent />
    </SidebarProvider>
  );
});

export default MobileApp;
